import { useEffect, useState, useContext, useMemo } from "react";
import { FilterService } from "primereact/api";
import { CustomAdsFilters } from "../../components/Context/AdsFilters";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { MultiSelect } from "primereact/multiselect";
import { Link } from "react-router-dom";
import { BsCloudArrowUp } from "react-icons/bs";
import * as moment from "moment";
import {
  organizeCostaZones,
  organizeZonesByGroup,
  PATRIMONIAL_GROUPS,
  RESIDENTIAL_GROUPS,
  RUSTIC_SINGULAR_GROUPS,
} from "../../functions/ads/ad-functions";
import {
  adStatusOptions,
  gvOperationCloseOptions,
  operationTypeOptions,
  ALL_BUILDING_TYPES,
  DEPT_VALID_TYPES,
  residentialZones,
  patrimonialZones,
  costaZones,
  rusticAndSingularZones,
} from "../../constants/ads-requests-constants";
import "moment/locale/es";
import "./AdsTable.scss";
import "./DataTableDemo.scss";
import { getAdsByFilter } from "../../api/ads.api";

const AdsTable = ({
  ads,
  setAds,
  setTotalElements,
  setTotalPages,
  sortField = "updatedAt", // Valor por defecto para evitar undefined
  sortOrder = -1, // Valor por defecto (descendente)
  setSortField = () => {}, // Función vacía por defecto para evitar errores al llamar
  setSortOrder = () => {},
  showAllZones = false,
}) => {
  const { adsFilters, storeAdsFilters } = useContext(CustomAdsFilters);
  const [rawZones, setRawZones] = useState({
    res: [],
    pat: [],
    costa: [],
    rus: [],
  });
  const [loader, setLoader] = useState(true);

  // 1. Registro de filtros y carga de zonas
  useEffect(() => {
    FilterService.register("INCLUDES", (value, filter) => {
      if (!filter?.length) return true;
      if (!value) return false;
      let newFilter = filter.map((e) => e.name);
      if (newFilter.includes("Ninguno")) return true;
      return newFilter.some((elem) => value.includes(elem));
    });

    Promise.all([
      residentialZones,
      patrimonialZones,
      costaZones,
      rusticAndSingularZones,
    ]).then(([res, pat, costa, rus]) => {
      setRawZones({
        res: organizeZonesByGroup(res, RESIDENTIAL_GROUPS),
        pat: organizeZonesByGroup(pat, PATRIMONIAL_GROUPS),
        costa: organizeCostaZones(costa),
        rus: organizeZonesByGroup(rus, RUSTIC_SINGULAR_GROUPS),
      });
      setLoader(false);
    });
  }, []);

  // PERSISTENCIA: Sincronizar el orden del contexto con el estado local al cargar
  useEffect(() => {
    if (setSortField && setSortOrder && adsFilters) {
      if (adsFilters.sortField) setSortField(adsFilters.sortField);
      if (adsFilters.sortOrder) {
        setSortOrder(adsFilters.sortOrder === "ASC" ? 1 : -1);
      }
    }
  }, []); // Solo al montar

  // 2. Lógica de zonas dinámicas
  const allZones = useMemo(() => {
    const format = (data, label) => {
      const groups = {};
      data.forEach((item) => {
        const key = `${label} - ${item.zone || "Otros"}`;
        if (!groups[key]) groups[key] = { label: key.toUpperCase(), items: [] };
        groups[key].items.push(item);
      });
      return Object.values(groups);
    };

    const dept = showAllZones ? "Todos" : adsFilters.department;

    if (dept === "Residencial") return format(rawZones.res, "RESIDENCIAL");
    if (dept === "Patrimonio") return format(rawZones.pat, "PATRIMONIO");
    if (dept === "Costa") return format(rawZones.costa, "COSTA");
    if (dept === "Campos Rústicos & Activos Singulares")
      return format(rawZones.rus, "RÚSTICO / SINGULAR");

    return [
      ...format(rawZones.res, "RESIDENCIAL"),
      ...format(rawZones.pat, "PATRIMONIO"),
      ...format(rawZones.costa, "COSTA"),
      ...format(rawZones.rus, "RÚSTICO / SINGULAR"),
    ];
  }, [adsFilters.department, rawZones, showAllZones]);

  useEffect(() => {
    const dept = adsFilters.department;
    const currentSelectedTypes = adsFilters.adBuildingType || [];

    if (dept && DEPT_VALID_TYPES[dept] && currentSelectedTypes.length > 0) {
      const allowedNames = DEPT_VALID_TYPES[dept];

      // Filtramos los tipos que YA NO pertenecen al nuevo departamento
      const validSelections = currentSelectedTypes.filter((type) =>
        allowedNames.includes(type.name),
      );

      // Si el usuario cambió de dpto y las selecciones ya no son válidas, limpiamos
      if (validSelections.length !== currentSelectedTypes.length) {
        onFilterChange("adBuildingType", validSelections);
      }
    }
  }, [adsFilters.department]);

  const filteredBuildingTypes = useMemo(() => {
    const dept = adsFilters.department;

    // Si hay un departamento específico seleccionado, filtramos ALL_BUILDING_TYPES
    if (dept && DEPT_VALID_TYPES[dept]) {
      const allowedNames = DEPT_VALID_TYPES[dept];
      return ALL_BUILDING_TYPES.filter((type) =>
        allowedNames.includes(type.name),
      );
    }

    // Si no hay departamento (Todos), mostramos la lista completa
    return ALL_BUILDING_TYPES;
  }, [adsFilters.department]);

  // 3. Formateo de datos
  const formattedAds = useMemo(() => {
    return ads.map((ad) => {
      const zoneNames = Array.isArray(ad.zone)
        ? ad.zone.map((z) => z.name).join(", ")
        : ad.zone;
      let direction = ad.adDirection;
      if (typeof direction === "object" && direction.address) {
        direction = Object.values(direction.address).filter(Boolean).join(" ");
      }
      return {
        ...ad,
        adDirection: direction,
        adBuildingType: Array.isArray(ad.adBuildingType)
          ? ad.adBuildingType.sort().join(", ")
          : ad.adBuildingType,
        adType: Array.isArray(ad.adType)
          ? ad.adType.sort().join(", ")
          : ad.adType,
        zone: zoneNames,
      };
    });
  }, [ads]);

  // Filtros para la tabla vinculados al contexto
  const filters = {
    adType: { value: adsFilters.adType || [], matchMode: "INCLUDES" },
    adBuildingType: {
      value: adsFilters.adBuildingType || [],
      matchMode: "INCLUDES",
    },
    gvOperationClose: {
      value: adsFilters.gvOperationClose || [],
      matchMode: "INCLUDES",
    },
    adStatus: { value: adsFilters.adStatus || [], matchMode: "INCLUDES" },
    zone: { value: adsFilters.zone || [], matchMode: "INCLUDES" },
  };

  const onFilterChange = async (field, value) => {
    const updatedFilters = { ...adsFilters, [field]: value };
    storeAdsFilters(updatedFilters);

    // Ejecutamos la búsqueda con los nuevos filtros y el orden actual
    const res = await getAdsByFilter({
      ...updatedFilters,
      sortField: sortField,
      sortOrder: sortOrder === 1 ? "ASC" : "DESC",
    });
    if (res) {
      setAds(res.ads);
      setTotalElements(res.pageInfo.totalElements);
      setTotalPages(res.pageInfo.totalPages);
    }
  };

  const onSort = async (e) => {
    const newSortField = e.sortField;
    const newSortOrder = e.sortOrder === 1 ? "ASC" : "DESC";

    // 1. Actualizamos el padre (UI)
    setSortField(newSortField);
    setSortOrder(e.sortOrder);

    // 2. Actualizamos el Contexto (Persistencia)
    const updatedAdsFilters = {
      ...adsFilters,
      sortField: newSortField,
      sortOrder: newSortOrder,
    };
    storeAdsFilters(updatedAdsFilters);

    // 3. Llamada a la API
    const response = await getAdsByFilter(updatedAdsFilters);
    if (response) {
      setAds(response.ads);
    }
  };

  const itemsTemplate = (option) => (
    <div className="p-multiselect-representative-option">
      <span className="image-text ">{option.name}</span>
    </div>
  );

  const renderMultiSelect = (field, options, grouped = false) => (
    <MultiSelect
      value={adsFilters[field] || []}
      options={options}
      itemTemplate={itemsTemplate}
      optionLabel="name"
      optionGroupLabel={grouped ? "label" : null}
      optionGroupChildren={grouped ? "items" : null}
      onChange={(e) => onFilterChange(field, e.value)}
      placeholder="Todos"
      className="p-column-filter"
      filter
      filterPlaceholder="Buscar..."
    />
  );

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header="Fecha de modificación"
          rowSpan={2}
          sortable
          field="updatedAt"
          style={{ width: "0%" }}
        />
        <Column header="Referencia" rowSpan={2} style={{ width: "0%" }} />
        <Column header="Dirección" rowSpan={2} style={{ width: "0.75%" }} />
        <Column header="Título" rowSpan={2} style={{ width: "1.5%" }} />
        <Column
          header="Estado anuncio"
          rowSpan={2}
          style={{ width: "0.10%" }}
          filter
          filterField="adStatus"
          filterElement={renderMultiSelect("adStatus", adStatusOptions)}
          showFilterMatchModes={false}
          showApplyButton={false}
          showClearButton={false}
        />
        <Column
          header="Cierre operación GV"
          rowSpan={2}
          style={{ width: "0%" }}
          filter
          filterField="gvOperationClose"
          filterElement={renderMultiSelect(
            "gvOperationClose",
            gvOperationCloseOptions,
          )}
          showFilterMatchModes={false}
          showApplyButton={false}
          showClearButton={false}
        />
        <Column header="Precio" colSpan={2} />
        <Column header="Superficie" rowSpan={2} style={{ width: "0%" }} />
        <Column
          header="Tipo de inmueble"
          rowSpan={2}
          style={{ width: "0.15%" }}
          filter
          filterField="adBuildingType"
          filterElement={renderMultiSelect(
            "adBuildingType",
            filteredBuildingTypes,
          )}
          showFilterMatchModes={false}
          showApplyButton={false}
          showClearButton={false}
        />
        <Column
          header="Tipo de anuncio"
          rowSpan={2}
          style={{ width: "0%" }}
          filter
          filterField="adType"
          filterElement={renderMultiSelect("adType", operationTypeOptions)}
          showFilterMatchModes={false}
          showApplyButton={false}
          showClearButton={false}
        />
        <Column
          header="Zona"
          rowSpan={2}
          style={{ width: "0.15%" }}
          filter
          filterField="zone"
          filterElement={renderMultiSelect("zone", allZones, true)}
          showFilterMatchModes={false}
          showApplyButton={false}
          showClearButton={false}
        />
        <Column header="Propietario" rowSpan={2} style={{ width: "0.15%" }} />
        <Column header="Consultor" rowSpan={2} style={{ width: "0.15%" }} />
      </Row>
      <Row>
        <Column
          header="Venta"
          colSpan={1}
          style={{ width: "0.08%" }}
          field="sale.saleValue"
          sortable
        />
        <Column
          header="Alquiler"
          colSpan={1}
          style={{ width: "0.08%" }}
          field="rent.rentValue"
          sortable
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <>
      {!loader ? (
        <DataTable
          value={formattedAds}
          headerColumnGroup={headerGroup}
          dataKey="_id"
          filters={filters}
          filterDisplay="menu"
          showGridlines
          removableSort
          rows={100}
          sortField={sortField} // Valor del padre
          sortOrder={sortOrder} // Valor del padre
          onSort={onSort} // Función que actualiza padre y contexto
          emptyMessage="No se ha encontrado ningún anuncio"
        >
          <Column
            field="updatedAt"
            body={(r) => moment(r.updatedAt).locale("es-ES").format("L")}
          />
          <Column
            field="adReference"
            body={(r) => (
              <Link
                style={{ textDecoration: "none", color: "inherit" }}
                to={`/anuncios/${r._id}`}
              >
                {r.adReference}
              </Link>
            )}
          />
          <Column
            field="adDirection"
            body={(r) => (
              <Link
                style={{ textDecoration: "none", color: "inherit" }}
                to={`/anuncios/${r._id}`}
              >
                {r.adDirection}
              </Link>
            )}
          />
          <Column
            field="title"
            body={(r) => (
              <Link
                style={{ textDecoration: "none", color: "inherit" }}
                to={`/anuncios/${r._id}`}
              >
                {r.title}
              </Link>
            )}
          />
          <Column field="adStatus" />
          <Column field="gvOperationClose" />
          <Column
            field="sale.saleValue"
            body={(r) =>
              r.sale?.saleValue
                ? Math.round(r.sale.saleValue)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                : ""
            }
          />
          <Column
            field="rent.rentValue"
            body={(r) =>
              r.rent?.rentValue
                ? Math.round(r.rent.rentValue)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €/mes"
                : ""
            }
          />
          <Column
            field="buildSurface"
            body={(r) =>
              r.buildSurface
                ? r.buildSurface
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " m²"
                : ""
            }
          />
          <Column field="adBuildingType" />
          <Column field="adType" />
          <Column field="zone" />
          <Column
            field="owner.fullName"
            body={(r) => (
              <Link to={`/contactos/${r.owner?._id}`}>{r.owner?.fullName}</Link>
            )}
          />
          <Column field="consultant.fullName" />
        </DataTable>
      ) : (
        <div style={{ height: 200 }}>
          <p style={{ lineHeight: 4 }}>Cargando anuncios...</p>
          <BsCloudArrowUp fontSize="2.5em" />
        </div>
      )}
    </>
  );
};

export default AdsTable;
